import React, { useEffect, useState, createContext, useContext } from 'react';
import { initAuth0, login as authLogin, logout as authLogout, getUser, isAuthenticated as checkAuth, getIdToken, getAccessToken, fetchUserProfile, subscribeToAuthChanges } from './utils/auth';
import { ensureUserDocument, UserProfile } from '../services/firebase';
import analyticsService, { ANALYTICS_EVENTS } from '../services/analytics';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
// Define the context type
type AuthContextType = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: any;
  userProfile: UserProfile | null;
  login: () => void;
  logout: () => void;
  getAuthToken: () => string | null;
  refreshAuthToken: () => Promise<string | null>;
};
// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
  user: null,
  userProfile: null,
  login: () => {},
  logout: () => {},
  getAuthToken: () => null,
  refreshAuthToken: async () => null
});
// Custom hook to use the context
export const useAuth = () => useContext(AuthContext);
// Provider component
export const AuthProvider = ({
  children,
  domain,
  clientId,
  redirectUri
}: {
  children: React.ReactNode;
  domain: string;
  clientId: string;
  redirectUri: string;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [lastAuthCheck, setLastAuthCheck] = useState<number>(0);
  // Initialize Auth and listen for Firebase auth state changes
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔧 AuthContext: Initializing authentication...');
        // Initialize Firebase Auth (this will handle URL tokens)
        await initAuth0();
        
        if (!auth) {
          console.error('❌ Firebase auth not available');
          setIsLoading(false);
          return;
        }
        
        // Listen for Firebase auth state changes directly
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          console.log('🔥 Firebase Auth State Changed:', firebaseUser ? 'User authenticated' : 'User signed out');
          
          if (firebaseUser) {
            // User is signed in
            const authUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
              photoURL: firebaseUser.photoURL || `https://api.dicebear.com/7.x/initials/svg?seed=${firebaseUser.email}`,
              role: 'free'
            };
            
            console.log('👤 Setting authenticated user');
            
            try {
              // Store user data and token
              const token = await firebaseUser.getIdToken();
              localStorage.setItem('auth_token', token);
              localStorage.setItem('auth_user', JSON.stringify(authUser));
              
              // Ensure user document exists in Firestore
              const profile = await ensureUserDocument(authUser);
              
              // Update context state
              setUser(authUser);
              setUserProfile(profile);
              setIsAuthenticated(true);
              
              // Track user identification for analytics
              analyticsService.setUser(authUser.uid, {
                email_verified: authUser.email ? 'true' : 'false',
                auth_provider: 'firebase',
                user_role: authUser.role || 'free'
              });
              
              console.log('✅ User authentication complete');
            } catch (error) {
              console.error('❌ Error processing authenticated user:', error);
              setUser(authUser); // Still set user even if document creation fails
              setUserProfile(null);
              setIsAuthenticated(true);
            }
          } else {
            // User is signed out
            console.log('❌ User not authenticated');
            setUser(null);
            setUserProfile(null);
            setIsAuthenticated(false);
            
            // Clear stored data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
          }
          
          setIsLoading(false);
          setLastAuthCheck(Date.now());
        });
        
        // Return cleanup function
        return unsubscribe;
      } catch (error) {
        console.error('❌ Error initializing authentication:', error);
        setAuthError('Failed to initialize authentication');
        setIsLoading(false);
      }
    };

    let unsubscribe: (() => void) | null = null;
    initializeAuth().then(cleanup => {
      unsubscribe = cleanup || null;
    });
    
    // Cleanup on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);
  // Periodic token validation (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated) return;
    const validateTokenPeriodically = () => {
      const idToken = getIdToken();
      if (!idToken) {
        console.log('⚠️ No ID token found during periodic check');
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
      try {
        // Check token expiration
        const tokenData = JSON.parse(atob(idToken.split('.')[1]));
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        // If token is expired or about to expire in the next 5 minutes
        if (expirationTime < currentTime + 300000) {
          console.log('⚠️ Token expired or about to expire, logging out');
          logout();
        }
      } catch (error) {
        console.error('❌ Error validating token:', error);
      }
      setLastAuthCheck(Date.now());
    };
    // Check token every 5 minutes
    const interval = setInterval(validateTokenPeriodically, 300000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);
  // Listen for storage changes (in case user logs in/out in another tab)
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'id_token' || e.key === 'auth_user') {
        // Re-check authentication status
        const authenticated = checkAuth();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          const authUser = getUser();
          if (authUser) {
            // Ensure user document exists
            try {
              const profile = await ensureUserDocument(authUser);
              setUserProfile(profile);
            } catch (error) {
              console.error('❌ Error ensuring user document on storage change:', error);
              setUserProfile(null);
            }
          }
          setUser(authUser);
        } else {
          setUser(null);
          setUserProfile(null);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  // Login function
  const login = () => {
    try {
      console.log('🔑 AuthContext: Starting login...');
      setAuthError(null);
      return authLogin();
    } catch (error) {
      console.error('❌ Error during login:', error);
      setAuthError('Login failed. Please try again.');
      throw error;
    }
  };
  // Logout function
  const logout = () => {
    try {
      console.log('🚪 AuthContext: Starting logout...');
      setAuthError(null);
      
      // Clear state immediately to prevent routing issues
      setUser(null);
      setUserProfile(null);
      setIsAuthenticated(false);
      
      // Track logout event
      analyticsService.logEvent(ANALYTICS_EVENTS.LOGOUT);
      
      // Use Firebase logout for all users
      return authLogout();
    } catch (error) {
      console.error('❌ Error during logout:', error);
      setAuthError('Logout failed. Please try again.');
      throw error;
    }
  };
  // Get auth token for API calls
  const getAuthToken = (): string | null => {
    // Try to get ID token first, then access token
    const idToken = getIdToken();
    if (idToken) return idToken;
    const accessToken = getAccessToken();
    return accessToken;
  };
  // Refresh token if needed
  const refreshAuthToken = async (): Promise<string | null> => {
    try {
      // For now, just return the current token
      // In a real implementation, this would call Auth0 to refresh the token
      return getAuthToken();
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      return null;
    }
  };
  // Context value
  const value = {
    isLoading,
    isAuthenticated,
    user,
    userProfile,
    login,
    logout,
    getAuthToken,
    refreshAuthToken
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};